import React, { useMemo, useState } from "react";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  LineController,
  PointElement,
  TimeScale,
  Tooltip,
} from "chart.js";
import "chartjs-adapter-date-fns";
import { Chart } from "react-chartjs-2";
import {
  format,
  eachWeekOfInterval,
  eachMonthOfInterval,
  eachDayOfInterval,
  addDays,
  differenceInCalendarDays,
  endOfMonth,
} from "date-fns";
import compactCurrencyFormatter from "../../../../utils/compact-currency-formatter";
import CustomTooltip from "./components/tooltip";
import style from "./ChargebackVolumeChart.module.scss";
import useCommonStore from "../../../../stores/common";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  LineController,
  Filler,
  Tooltip,
  Legend,
  TimeScale,
);

const verticalLinePlugin = {
  id: "horizontalLineOnHover",
  beforeDatasetsDraw: (chart) => {
    if (chart.tooltip?._active && chart.tooltip._active.length) {
      const ctx = chart.ctx;
      ctx.save();
      const activePoint = chart.tooltip._active[0];
      const x = activePoint.element.x;
      const top = chart.chartArea.top;
      const bottom = chart.chartArea.bottom;
      const dotSpacing = 8;
      for (let y = top; y < bottom; y += dotSpacing) {
        ctx.beginPath();
        ctx.ellipse(
          x,
          y,
          1.05, // horizontal radius
          1.75, // this elongates the dot
          0,
          0,
          2 * Math.PI,
        );
        ctx.fill();
      }
      ctx.restore();
    }
  },
};

const getBackgroundColorGradient = (context) => {
  const { chart } = context;
  const { ctx, chartArea } = chart;

  if (!chartArea) return null;

  const gradient = ctx.createLinearGradient(
    0,
    chartArea.top,
    0,
    chartArea.bottom,
  );
  gradient.addColorStop(0, "rgba(76, 112, 239, 0.20)");
  gradient.addColorStop(1, "rgba(76, 112, 239, 0.05)");
  return gradient;
};

const generateLabels = (rangeType, startDate, endDate) => {
  if (!startDate || !endDate) return [];

  const start = new Date(startDate);
  const end = new Date(endDate);
  const startYear = start.getFullYear();

  switch (rangeType) {
    case "MTD":
    case "MONTH": {
      // Sub-week ranges (early MTD) collapse to a single weekly bucket — show daily.
      if (differenceInCalendarDays(end, start) + 1 < 7) {
        return eachDayOfInterval({ start, end }).map((day) => ({ day }));
      }
      const weekStarts = eachWeekOfInterval(
        { start, end },
        { weekStartsOn: 1 },
      );
      return weekStarts.map((weekStart) => ({
        weekStart,
        weekEnd: addDays(weekStart, 6),
      }));
    }

    case "YTD": {
      const monthStarts = eachMonthOfInterval({
        start: new Date(startYear, 0, 1),
        end,
      });
      return monthStarts.map((monthStart) => ({
        monthStart,
        monthEnd: endOfMonth(monthStart),
      }));
    }

    case "LTY": {
      const currentYear = new Date().getFullYear();
      const lastYear = currentYear - 1;
      const lastYearMonthStarts = eachMonthOfInterval({
        start: new Date(lastYear, 0, 1),
        end: new Date(lastYear, 11, 31),
      });
      return lastYearMonthStarts.map((monthStart) => ({
        monthStart,
        monthEnd: endOfMonth(monthStart),
      }));
    }

    default:
      return [];
  }
};

const formatPeriod = (dateLabel, rangeType) => {
  if (dateLabel.day) {
    return format(dateLabel.day, "d MMM");
  }
  if (["MONTH", "MTD"].includes(rangeType)) {
    return `${format(dateLabel.weekStart, "d")}-${format(dateLabel.weekEnd, "d MMM, yy")}`;
  }
  return format(dateLabel.monthStart, "MMM, yy");
};

function ChargebackVolumeChart({ data, filter }) {
  const chargebacksData = data;
  const chargebacksFilterYear = filter?.year;
  const chargebacksFilterType = filter?.type;
  const dateRange = useCommonStore((state) => state.dateRange);
  const rangeType = useCommonStore((state) => state.dateRange.rangeType);

  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    data: null,
  });

  const labels = useMemo(
    () => generateLabels(rangeType, dateRange.startDate, dateRange.endDate),
    [rangeType, dateRange.startDate, dateRange.endDate],
  );

  const chartData = useMemo(
    () => ({
      labels: labels.map((label) => formatPeriod(label, rangeType)),
      datasets: [
        {
          type: "line",
          label: "Transactions",
          data: chargebacksData.transactions,
          backgroundColor: getBackgroundColorGradient,
          borderColor: "rgba(76, 112, 239, 0.05)",
          fill: true,
          tension: 0.4,
          yAxisID: "y",
          borderWidth: 1,
          pointRadius: 0,
          pointHoverRadius: 0,
        },
        {
          type: "line",
          label: "Chargebacks",
          data: chargebacksData.chargebacks,
          borderColor: "rgba(224, 59, 36, 1)",
          backgroundColor: "rgba(224, 59, 36, 1)",
          borderWidth: 3,
          tension: 0.4,
          yAxisID: "y",
          pointRadius: 0,
        },
        // {
        //   type: "bar",
        //   borderRadius: 6,
        //   label: "DinersClub",
        //   data: data.diners,
        //   backgroundColor: "#0ea5e9",
        //   yAxisID: "y",
        // },
        // NOTE: Add more datasets here for card types
      ],
    }),
    [chargebacksData, labels],
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: "index",
        intersect: false,
      },
      stacked: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
          external: (context) => {
            const tooltipModel = context.tooltip;
            if (
              !tooltipModel ||
              !tooltipModel.opacity ||
              !tooltipModel.dataPoints?.length
            ) {
              setTooltip((prev) => ({ ...prev, visible: false }));
              return;
            }

            const chart = context.chart;
            const chartArea = chart.chartArea;
            const tooltipWidth = 180;
            const tooltipHeight = 60;
            let x = tooltipModel.caretX;
            let y = tooltipModel.caretY;

            let xShifted = x + 20;

            // If too close to the right edge, show to the left of the cursor
            if (x + tooltipWidth + 20 > chartArea.right) {
              xShifted = x - tooltipWidth * 1.5;
            }

            // If too close to the left edge, clamp to left edge + margin
            if (xShifted < chartArea.left + 20) {
              xShifted = chartArea.left + 20;
            }

            let yShifted = y;
            if (y + tooltipHeight > chartArea.bottom) {
              yShifted = chartArea.bottom - tooltipHeight - 20;
            }
            if (yShifted < chartArea.top + 20) {
              yShifted = chartArea.top + 20;
            }

            const dataPoint = tooltipModel.dataPoints[0];
            const dataIndex = dataPoint.dataIndex;
            const tooltipData = {
              transactions: chargebacksData?.transactions?.[dataIndex] || 0,
              chargebacks: chargebacksData?.chargebacks?.[dataIndex] || 0,
              type: chargebacksFilterType,
              year: chargebacksFilterYear,
              period: formatPeriod(labels[dataIndex], rangeType),
            };
            setTooltip({
              visible: true,
              x: xShifted,
              y: yShifted,
              data: tooltipData,
            });
          },
        },
      },
      layout: {
        padding: 0,
      },
      scales: {
        x: {
          type: "category",
          offset: false,
          title: {
            display: false,
          },
          ticks: {
            callback: function (value, index, ticks) {
              return formatPeriod(labels[index], rangeType);
            },
            color: "#A4A3A8",
            drawTicks: false,
            maxRotation: 0,
            minRotation: 0,
            font: {
              family: "Work Sans, sans-serif",
              size: 12,
              weight: "400",
            },
          },
          grid: {
            drawOnChartArea: false,
          },
        },
        y: {
          type: "linear",
          beginAtZero: true,
          suggestedMin: 0,
          ticks: {
            callback: (val) => {
              if (chargebacksFilterType === "amount") {
                return compactCurrencyFormatter.format(val);
              }
              return val;
            },
            color: "#A4A3A8",
            font: {
              family: "Work Sans, sans-serif",
              size: 12,
              weight: "400",
            },
          },
          grid: {
            drawBorder: false,
          },
          border: {
            display: false,
          },
        },
      },
    }),
    [
      chargebacksFilterType,
      chargebacksFilterYear,
      chargebacksData,
      rangeType,
      labels,
    ],
  );

  return (
    <div className={style.body}>
      <Chart
        type="bar"
        data={chartData}
        options={options}
        plugins={[verticalLinePlugin]}
        height={270}
      />
      {tooltip.visible && (
        <CustomTooltip x={tooltip.x} y={tooltip.y} data={tooltip.data} />
      )}
    </div>
  );
}

export default ChargebackVolumeChart;
